import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import isString from 'lodash/isString';
import { connect } from 'react-redux';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CustomBox from '../common/CustomBox';
import ProviderAvatar from '../providers/ProviderAvatar';
import WizardStepper from './WizardStepper';
import PatientSchedulingServiceForm from './PatientSchedulingServiceForm';
import PatientSchedulingLocationForm from './PatientSchedulingLocationForm';
import PatientSchedulingTimeForm from './PatientSchedulingTimeForm';
import PatientSchedulingLocationTypeForm from './PatientSchedulingLocationTypeForm';
import NewOrExistingPatient from './NewOrExistingPatient';
import { useGetSessionTypesQuery } from '../../redux/services/sessionTypeApi';
import { useUpdateVisitorMutation } from '../../redux/services/visitorApi';
import {formatProviderName} from '../../helpers/providers';
import { 
  setDate, 
  setTime, 
  setServiceType,
  setSessionTypeId, 
  setProviderId,
  setProviderAvailabilityId,
  setLocationId,
  setLocationType,
  setScheduleManually,
  setTimeCreated,
  reset,
} from '../../redux/reducers/newAppointmentForm';

const steps = ({
  provider,
  formState,
  serviceCategories,
  sessionTypes,
  setField,
  saveDate,
  saveLocationType,
  saveLocationId,
  saveTime,
  saveServiceCategory,
  saveSessionTypeId,
  saveProviderAvailabilityId,
  saveScheduleManually,
  saveTimeCreated,
  onScheduleManually,
  next,
  navigate,
  updateVisitor,
}) => [
  {
    label: 'Account',
    Component: NewOrExistingPatient,
    onSubmit: (e, isNewPatient) => {
      if (isNewPatient) {
        next();
      } else {
        navigate('/login');
      }
    },
  },
  {
    label: 'Service',
    Component: PatientSchedulingServiceForm,
    provider,
    providerId: provider?.id,
    formState,
    allowedServiceCategories: serviceCategories,
    sessionTypes,
    onSubmit: async (e, service) => {
      console.log('Selected service:', service);
      setField('serviceCategory')(service);
      const sessionType = sessionTypes.find(st => st.service_category === service);
      saveSessionTypeId(sessionType.id);
      saveServiceCategory(service);
      await updateVisitor({
        scheduling_step: 'SELECTED_SERVICE_CATEGORY',
        service_category: service,
      });
      next();
    },
  },
  {
    label: 'Location',
    Component: PatientSchedulingLocationForm,
    onSubmit: async (e, location) => {
      console.log('Selected location type:', location);
      setField('location')(location);
      await updateVisitor({
        lat: location?.lat,
        lon: location?.lon,
        scheduling_step: 'SELECTED_LOCATION',
      });
      // saveLocationType(); // TODO
      // saveLocationId(); // TODO
      next();
    },
  },
  {
    label: 'Schedule',
    Component: props => (
      <Stack spacing={1} alignItems='center'>
        <Typography variant='h2'>Step 4: Schedule a Time</Typography>
        <Typography>What time works best for you?</Typography>
        <Container maxWidth='xs'>
          <PatientSchedulingTimeForm 
            providerId={provider?.id}
            serviceCategory={formState.serviceCategory}
            lat={formState?.location?.lat}
            lon={formState?.location?.lon}
            distance={30}
            defaultDate={
              formState?.date && formState?.time
              ? moment(
                `${formState.date} ${formState.time}`,
                'MM/DD/yyyy hh:mm a'
              ).toDate()
              : moment().toDate()
            }
            hideLocationTypes={true}
            onSendMessage={() => {
              saveScheduleManually(true);
              saveDate(null);
              saveTime(null);
              saveProviderAvailabilityId(null);
              saveLocationType(null);
              navigate(`/new-account?alreadyScheduled=true&selectedProviderId=${provider?.id}&version=v2`);
            }}
            {...props} 
          />
        </Container>
      </Stack>
    ),
    onSubmit: async (e, {isoString, providerAvailabilityId}) => {
      const start = isoString;
      if (start) {
        const dateStr = moment(start).format('MM/DD/yyyy');
        const timeStr = moment(start).format('hh:mm a');
        saveDate(dateStr);
        saveTime(timeStr);
        saveProviderAvailabilityId(providerAvailabilityId);
        setField('start_time')(start);
        setField('providerAvailabilityId')(providerAvailabilityId);
        await updateVisitor({
          scheduling_step: 'SELECTED_TIME',
          session_time_requested: moment(start).toISOString(),
        });
      }
      next();
    },
  },
  {
    label: 'Location',
    Component: PatientSchedulingLocationTypeForm,
    onSubmit: async (e, location) => {
      const {location_type, location_id} = location || {};
      saveTimeCreated(moment().toISOString());
      let trackedUpdates = {
        scheduling_step: 'COMPLETED_PRESIGNUP',
      };
      if (isString(location_type)) {
        setField('location_type')(location_type);
        saveLocationType(location_type);
        trackedUpdates.location_type = location_type;
      } else {
        setField('location_type')(null);
        saveLocationType(null);
      }
      if (location_id) {
        setLocationId(location_id);
        setField('location_id')(location_id);
        saveLocationId(location_id);
        trackedUpdates.location_id = location_id;
      } else {
        setLocationId(null);
        setField('location_id')(null);
        saveLocationId(null);
      }
      await updateVisitor(trackedUpdates);
      navigate(`/new-account?alreadyScheduled=true&selectedProviderId=${provider?.id}&version=v2`);
    },  
  }
];

const BookingFromPublicProviderProfileForm = ({
  provider,
  saveDate,
  saveTime,
  saveSessionTypeId,
  saveProviderAvailabilityId,
  saveLocationId,
  saveLocationType,
  saveScheduleManually,
  saveProviderId,
  saveServiceCategory,
  saveTimeCreated,
  reset,
  borderRadius,
  boxStyles = {minHeight: '90vh', borderRadius},
  uuid,
}) => {
  const providerId = provider?.id;
  const [stepIndex, setStepIndex] = useState(0);
  const [formState, setFormState] = useState({
    serviceCategory: null,
    location: null,
    datetime: null,
    locationType: null,
    locationId: null,
    providerAvailabilityId: null,
  });
  const navigate = useNavigate();
  const [updateVisitor] = useUpdateVisitorMutation();
  const sessionTypeQuery = useGetSessionTypesQuery({
    provider_id: provider?.id,
    session_type_version: '2.0',
  }, {skip: !provider?.id});
  useEffect(() => {
      reset();
    }, []);
  useEffect(() => {
    if (providerId) saveProviderId(providerId);
  }, [providerId]);
  const _steps = steps({
    provider, 
    formState,
    serviceCategories: (sessionTypeQuery?.data || []).map(st => st.service_category) || [],
    sessionTypes: sessionTypeQuery?.data || [],
    // methods
    saveDate,
    saveTime,
    saveSessionTypeId,
    saveProviderAvailabilityId,
    saveLocationId,
    saveLocationType,
    saveScheduleManually,
    saveServiceCategory,
    saveTimeCreated,
    updateVisitor: async params => await updateVisitor({uuid, ...params}),
    setField: (field) => value => setFormState({...formState, [field]: value}),
    next: () => setStepIndex(stepIndex + 1),
    navigate,
  });
  const stepConfig = _steps[stepIndex];
  const FormComponent = stepConfig.Component;
  const childProps = {
    ...stepConfig,
    stepNum: stepIndex,
    providerId: provider?.id,
    providerAvailabilityId: formState?.providerAvailabilityId,
    serviceCategory: formState.serviceCategory,
    lat: formState?.location?.lat,
    lon: formState?.location?.lon,
  };
  return (
    <Box style={{...boxStyles, overflowY: 'auto'}}>
      <WizardStepper
        currentStepIndex={stepIndex}
        steps={_steps} 
        hideTitles={true}
        onBack={stepIndex > 0 ? () => setStepIndex(stepIndex - 1) : null}
        borderRadius={
          boxStyles?.borderRadius
          ? `${boxStyles.borderRadius} ${boxStyles.borderRadius} 0px 0px`
          : null
        }
      />
      <Box pr={2} pl={2} pb={2}>
        <CustomBox mb={2}>
          <Stack p={1}>
            <Stack direction='row' alignItems='center' gap={1}>
              <ProviderAvatar provider={provider} />
              <Stack>
                <Typography variant='h3' align='center'>
                  Schedule with {formatProviderName(provider)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </CustomBox>
        <FormComponent {...childProps} />
      </Box>
    </Box>
  );
};

const mapState = state => ({
  uuid: state?.analytics?.visitorUUID,
});

const mapDispatch = dispatch => ({
  saveDate: dateStr => dispatch(setDate(dateStr)),
  saveTime: timeStr => dispatch(setTime(timeStr)),
  saveServiceCategory: serviceCategory => dispatch(setServiceType(serviceCategory)),
  saveProviderId: providerId => dispatch(setProviderId(providerId)),
  saveSessionTypeId: sessionTypeId => dispatch(setSessionTypeId(sessionTypeId)),
  saveProviderAvailabilityId: providerAvailabilityId => 
    dispatch(setProviderAvailabilityId(providerAvailabilityId)),
  saveLocationId: locationId => dispatch(setLocationId(locationId)),
  saveLocationType: locationType => dispatch(setLocationType(locationType)),
  saveScheduleManually: isManual => dispatch(setScheduleManually(isManual)),
  saveTimeCreated: timeCreated => dispatch(setTimeCreated(timeCreated)),
  reset: () => dispatch(reset()),
});

export default connect(mapState, mapDispatch)(BookingFromPublicProviderProfileForm);